import * as Tooltip from "@radix-ui/react-tooltip";
import React, { createContext, useContext } from "react";

/** How long the pointer rests on a trigger before the first tooltip opens. */
export const KUIKA_TOOLTIP_DELAY_MS: number = 300;
/** How long after a tooltip closes the next one still opens at once ("warm-up"). */
export const KUIKA_TOOLTIP_SKIP_DELAY_MS: number = 300;

// Radix offers no way to ask whether a Tooltip.Provider is above, and Tooltip.Root throws without one,
// so the root provider announces itself through this context.
const KuikaTooltipRootContext: React.Context<boolean> = createContext<boolean>(false);

export interface IKuikaTooltipProviderProps {
  children: React.ReactNode;
  /**
   * Close each tooltip as soon as the pointer leaves its trigger, instead of keeping it open while the
   * pointer travels toward it. Radix tracks that travel with `pointermove` on this document, which a
   * pointer moving into an iframe never sends — so a tooltip over the canvas stayed up until the pointer
   * came back out. For a surface laid over an iframe (the canvas selection bar).
   */
  disableHoverableContent?: boolean;
}

/**
 * The app's one tooltip provider, mounted at each React root (index.tsx, login-entry.tsx). Every
 * KuikaTooltip under it shares one warm-up: once a tooltip has opened, moving to a neighbouring
 * trigger opens the next one at once instead of waiting the delay again — the toolbar behaviour.
 * Tooltips in portals (modals, popovers) share it too.
 *
 * A KuikaTooltip rendered outside it (a test, a detached render) still works on a provider of its own,
 * without the warm-up. A surface may nest its own (see `disableHoverableContent`); its tooltips then
 * share a warm-up among themselves.
 */
const KuikaTooltipProvider = ({ children, disableHoverableContent }: IKuikaTooltipProviderProps): JSX.Element => (
  <Tooltip.Provider
    delayDuration={KUIKA_TOOLTIP_DELAY_MS}
    skipDelayDuration={KUIKA_TOOLTIP_SKIP_DELAY_MS}
    disableHoverableContent={disableHoverableContent}
  >
    <KuikaTooltipRootContext.Provider value={true}>{children}</KuikaTooltipRootContext.Provider>
  </Tooltip.Provider>
);

/** Whether a KuikaTooltipProvider is above — a KuikaTooltip then joins it instead of opening its own. */
export const useHasKuikaTooltipProvider = (): boolean => useContext(KuikaTooltipRootContext);

export default KuikaTooltipProvider;
